import { useQueries } from '@tanstack/react-query';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { Interval } from '@ui/constants/poll';
import { useDataChainId } from '@ui/hooks/datachain';
import { SupportedSymbol } from '@ui/hooks/price/priceLatest/store';
import {
  usePriceLatest,
  usePriceMapLatest,
} from '@ui/hooks/price/usePriceLatest';
import { toReadableValue } from '@ui/lib/bignumber';
import { getAnyCollateralConfigFacet } from '@ui/services/contract/contract-instances/getAnyCollateralConfigFacet';
import { getVaultFacet } from '@ui/services/contract/contract-instances/getVaultFacet';
import { PayablePoolToken } from '@ui/types/token';
import BigNumber from 'bignumber.js';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import { usePublicClient } from 'wagmi';

import { useDirectCollateralOpenStatus } from './useDirectCollateralOpenStatus';

export const usePayTokenPriceLatest = (
  payToken: PayablePoolToken | undefined,
  refetchIntervalMs?: number,
) => {
  const directCollateralStatus = useDirectCollateralOpenStatus(payToken);
  const defaultPrice = usePriceLatest(
    payToken?.symbol as SupportedSymbol | undefined,
    refetchIntervalMs,
  );

  if (payToken?.isDirectCollateral) {
    return directCollateralStatus.price;
  }

  return defaultPrice;
};

export const getPayTokenPriceMapKey = (payToken: PayablePoolToken) =>
  payToken.address.toLowerCase();

export const usePayTokenPriceLatestMap = (
  payTokens: PayablePoolToken[] | undefined,
  refetchIntervalMs?: number,
) => {
  const chainId = useDataChainId();
  const client = usePublicClient({ chainId });
  const { data: defaultPriceMap } = usePriceMapLatest(refetchIntervalMs);

  const directCollateralTokens = useMemo(
    () =>
      uniqBy(
        (payTokens ?? []).filter((token) => token.isDirectCollateral),
        (token) => token.address.toLowerCase(),
      ),
    [payTokens],
  );

  const directCollateralQueries = useQueries({
    queries: directCollateralTokens.map((token) => ({
      refetchInterval: Interval.FAST,
      queryKey: [
        'AnyCollateral.openStatus',
        chainId,
        client,
        token.address,
      ],
      queryFn: async () => {
        if (!client) return undefined;

        const anyCollateralConfigFacet = getAnyCollateralConfigFacet({
          chainId,
          publicClient: client,
        });
        const vaultFacet = getVaultFacet({
          chainId,
          publicClient: client,
        });

        const [
          directCollateralEnabled,
          collateralTokenEnabled,
          collateralOracleConfig,
          collateralOpenCap,
          marginToken,
        ] = await Promise.all([
          anyCollateralConfigFacet.read.isDirectCollateralEnabled(),
          anyCollateralConfigFacet.read.isCollateralTokenEnabled([
            token.address,
          ]),
          anyCollateralConfigFacet.read.getCollateralOracleConfig([
            token.address,
          ]),
          anyCollateralConfigFacet.read.getCollateralOpenCap([token.address]),
          vaultFacet.read.getTokenForTrading([token.address]),
        ]);
        const maxOpenAmount = toReadableValue(
          collateralOpenCap.maxOpenAmount,
          token.decimals,
        );
        const usedOpenAmount = toReadableValue(
          collateralOpenCap.usedOpenAmount,
          token.decimals,
        );
        const remainingOpenAmount = maxOpenAmount.isZero()
          ? undefined
          : BigNumber.maximum(maxOpenAmount.minus(usedOpenAmount), 0);

        return {
          directCollateralEnabled,
          collateralTokenEnabled,
          collateralOracleEnabled: collateralOracleConfig.enabled,
          openCap: {
            maxOpenAmount,
            usedOpenAmount,
            remainingOpenAmount,
            isUnlimited: maxOpenAmount.isZero(),
          },
          marginToken,
          price: toReadableValue(marginToken.price, USD_DECIMALS),
        };
      },
      enabled: !!client,
    })),
  });

  const directCollateralPriceMap = useMemo(() => {
    return Object.fromEntries(
      directCollateralTokens
        .map((token, index) => {
          const price = directCollateralQueries[index]?.data?.price;

          return price
            ? [getPayTokenPriceMapKey(token), price]
            : undefined;
        })
        .filter((entry): entry is [string, BigNumber] => !!entry),
    );
  }, [directCollateralQueries, directCollateralTokens]);

  return useMemo(() => {
    return Object.fromEntries(
      (payTokens ?? []).map((token) => {
        const key = getPayTokenPriceMapKey(token);
        const price = token.isDirectCollateral
          ? directCollateralPriceMap[key]
          : defaultPriceMap?.[token.symbol as SupportedSymbol];

        return [key, price];
      }),
    ) as Partial<Record<string, BigNumber>>;
  }, [defaultPriceMap, directCollateralPriceMap, payTokens]);
};
